import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import {
  Field,
  FormSection,
  FormShell,
  FormShellSection,
  Notice,
  RowCard,
  SaveBar,
  SaveBarStatus,
} from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useFormDirty } from '../../hooks/useFormDirty';
import { useBeforeUnloadWarning } from '../../hooks/useBeforeUnloadWarning';
import { useConfirmDiscard } from '../../hooks/useConfirmDiscard';
import {
  listConsumables,
  isChemical,
  type Consumable,
  type ConsumableBase,
  type ConsumableType,
} from '../../services/consumableService';
import {
  createProcurement,
  uploadProcurementAttachment,
  type CreateProcurementBody,
  type UploadedAttachment,
} from '../../services/procurementService';
import QuickCreateConsumableModal from './QuickCreateConsumableModal';

/**
 * Spec B Task 7.2 — manual procurement entry.
 *
 * Four FormSection cards (source / product / quantity / financials), a
 * sticky SaveBar at the bottom, and an inline QuickCreateConsumableModal so
 * the operator never has to leave the form to add a missing product.
 *
 * Source-document attachments (PDF / PNG / JPG) are uploaded BEFORE submit
 * via `uploadProcurementAttachment`; the returned metadata is bundled into
 * the POST /procurement body so the resulting transaction owns its files.
 */

const ACCEPTED_ATTACHMENT_TYPES = '.pdf,.png,.jpg,.jpeg';
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024; // 10 MB — mirrors backend cap

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// Backend exports `QUANTITY_UNITS_BY_TYPE` from ConsumableTransaction.js; we
// duplicate it here for the unit dropdown. TODO(Phase 8): share from a
// single source if/when the consolidated catalogue lands.
const QUANTITY_UNITS_BY_TYPE: Record<ConsumableType, string[]> = {
  chemical:   ['L', 'ml', 'kg', 'g'],
  fertilizer: ['kg', 't', 'bag'],
  ppe:        ['pair', 'box', 'unit'],
  diesel:     ['L', 'drum'],
};

const todayISODate = () => new Date().toISOString().slice(0, 10);

const ConsumableProcurementFormPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // `?from=spray-inventory` lets the Add-Stock entry on the Spray Stock Levels
  // page round-trip cleanly: Cancel returns the user to where they came from
  // rather than the procurement list they never visited.
  const [searchParams] = useSearchParams();
  const cameFromSprayInventory = searchParams.get('from') === 'spray-inventory';

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('consumables.title'), to: '/consumables/catalog' },
    { label: t('consumables.procurement.title'), to: '/consumables/procurement' },
    { label: t('consumables.procurement.newProcurement') },
  ]);

  // ── product catalogue (for picker + new-product splice) ──────────────
  const [products, setProducts] = useState<Consumable[]>([]);

  const loadProducts = () => {
    listConsumables()
      .then(setProducts)
      .catch(() => setProducts([]));
  };

  useEffect(() => {
    loadProducts();
  }, []);

  // ── form state ───────────────────────────────────────────────────────
  const [date, setDate] = useState<string>(todayISODate());
  const [supplier, setSupplier] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');

  const [productId, setProductId] = useState('');
  // `?consumableType=chemical` (from the Spray Stock Levels Add-Stock button)
  // seeds the QuickCreate modal's default type so the operator doesn't have
  // to retoggle the radio when adding a new product mid-flow. The value is
  // overwritten by `handleSelectProduct` as soon as a product is chosen.
  const initialConsumableType: ConsumableType | '' = (() => {
    const raw = searchParams.get('consumableType');
    if (raw === 'chemical' || raw === 'ppe' || raw === 'fertilizer' || raw === 'diesel') {
      return raw;
    }
    return '';
  })();
  const [consumableType, setConsumableType] =
    useState<ConsumableType | ''>(initialConsumableType);

  const [quantity, setQuantity] = useState('');
  const [quantityUnit, setQuantityUnit] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  const [unitPrice, setUnitPrice] = useState('');
  const [totalPrice, setTotalPrice] = useState('');

  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  // ── attachments (Task 7.4) ───────────────────────────────────────────
  // We hold the uploaded metadata plus a client-side `size` (the upload
  // endpoint doesn't echo size, but the picker gives us File.size for free
  // and the RowCard subtitle benefits from showing it). On submit we strip
  // size off — the backend's attachments sub-schema is { s3Key, fileName,
  // mimeType, label, uploadedAt }.
  type LocalAttachment = UploadedAttachment & { size: number };
  const [attachments, setAttachments] = useState<LocalAttachment[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [attachmentError, setAttachmentError] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handlePickAttachments = () => {
    fileInputRef.current?.click();
  };

  const handleFilesPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Always reset the input so the user can re-pick the same file later.
    e.target.value = '';
    if (files.length === 0) return;
    setAttachmentError('');

    // Pre-flight size check; reject the offending file(s) without uploading.
    const tooBig = files.filter(f => f.size > ATTACHMENT_MAX_BYTES);
    const okFiles = files.filter(f => f.size <= ATTACHMENT_MAX_BYTES);
    if (tooBig.length > 0) {
      setAttachmentError(
        t('consumables.procurement.form.field.attachmentTooLarge', {
          defaultValue: 'One or more files exceed the 10 MB limit and were skipped.',
        }),
      );
    }
    if (okFiles.length === 0) return;

    setUploadingCount(c => c + okFiles.length);
    try {
      const results = await Promise.all(
        okFiles.map(async file => {
          const uploaded = await uploadProcurementAttachment(file);
          return { ...uploaded, size: file.size };
        }),
      );
      setAttachments(prev => [...prev, ...results]);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setAttachmentError(message);
    } finally {
      setUploadingCount(c => Math.max(0, c - okFiles.length));
    }
  };

  const handleRemoveAttachment = (s3Key: string) => {
    setAttachments(prev => prev.filter(a => a.s3Key !== s3Key));
  };

  // Selecting a product seeds the consumableType and resets the unit to
  // the first valid option for that type, so the user never sees a stale
  // unit from a prior type selection.
  const handleSelectProduct = (id: string) => {
    setProductId(id);
    const picked = products.find(p => p._id === id);
    if (!picked) {
      setConsumableType('');
      setQuantityUnit('');
      return;
    }
    setConsumableType(picked.consumableType);
    const units = QUANTITY_UNITS_BY_TYPE[picked.consumableType];
    setQuantityUnit(units?.[0] ?? '');
  };

  // When QuickCreateConsumableModal succeeds, splice the new doc into the
  // dropdown options and auto-select it so the user lands back in flow.
  const handleProductCreated = (created: ConsumableBase) => {
    const next: Consumable = created as Consumable;
    setProducts(prev =>
      prev.find(p => p._id === next._id) ? prev : [...prev, next]
    );
    setProductId(next._id);
    setConsumableType(next.consumableType);
    const units = QUANTITY_UNITS_BY_TYPE[next.consumableType];
    setQuantityUnit(units?.[0] ?? '');
    setQuickCreateOpen(false);
  };

  // ── derived ──────────────────────────────────────────────────────────
  const selectedProduct = useMemo(
    () => products.find(p => p._id === productId) ?? null,
    [products, productId],
  );

  const unitOptions = consumableType
    ? QUANTITY_UNITS_BY_TYPE[consumableType]
    : [];

  const quantityNum = Number(quantity);
  const unitPriceNum = Number(unitPrice);
  const totalPriceNum = Number(totalPrice);

  const allRequiredFilled =
    date.trim() !== '' &&
    supplier.trim() !== '' &&
    referenceNumber.trim() !== '' &&
    productId !== '' &&
    quantity.trim() !== '' &&
    Number.isFinite(quantityNum) &&
    quantityNum > 0 &&
    quantityUnit.trim() !== '' &&
    unitPrice.trim() !== '' &&
    Number.isFinite(unitPriceNum) &&
    unitPriceNum >= 0 &&
    totalPrice.trim() !== '' &&
    Number.isFinite(totalPriceNum) &&
    totalPriceNum >= 0;

  // ── Task 7.3: soft warning when totalPrice drifts > 1% from qty × unitPrice.
  // Soft = informational only; SaveBar stays enabled. The warning is only
  // computed once all three figures are meaningful so we don't shout at the
  // user mid-typing.
  const computedTotal = quantityNum * unitPriceNum;
  const mismatchDelta = Math.abs(totalPriceNum - computedTotal);
  const showTotalMismatch =
    Number.isFinite(quantityNum) && quantityNum > 0 &&
    Number.isFinite(unitPriceNum) && unitPriceNum > 0 &&
    Number.isFinite(totalPriceNum) && totalPriceNum > 0 &&
    computedTotal > 0 &&
    mismatchDelta / computedTotal > 0.01;

  const { dirty } = useFormDirty(
    {
      date: todayISODate(),
      supplier: '',
      referenceNumber: '',
      productId: '',
      // Seeded from the URL above, so the dirty baseline must match — otherwise
      // the SaveBar would show "Unsaved changes" the instant the user lands here.
      consumableType: initialConsumableType,
      quantity: '',
      quantityUnit: '',
      expiryDate: '',
      unitPrice: '',
      totalPrice: '',
      attachmentCount: 0,
    },
    {
      date,
      supplier,
      referenceNumber,
      productId,
      consumableType,
      quantity,
      quantityUnit,
      expiryDate,
      unitPrice,
      totalPrice,
      attachmentCount: attachments.length,
    },
  );
  useBeforeUnloadWarning(dirty);
  const confirmDiscard = useConfirmDiscard();

  // ── actions ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!allRequiredFilled || saving || uploadingCount > 0) return;
    setSaving(true);
    setApiError('');
    const body: CreateProcurementBody = {
      productId,
      date,
      supplier: supplier.trim(),
      quantity: quantityNum,
      quantityUnit,
      unitPrice: unitPriceNum,
      totalPrice: totalPriceNum,
      referenceNumber: referenceNumber.trim(),
      source: 'manual',
      ...(expiryDate ? { expiryDate } : {}),
      ...(attachments.length > 0
        ? {
            attachments: attachments.map(({ s3Key, fileName, mimeType, uploadedAt }) => ({
              s3Key,
              fileName,
              mimeType,
              uploadedAt,
            })),
          }
        : {}),
    };
    try {
      const saved = await createProcurement(body);
      navigate(`/consumables/procurement/${saved._id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setApiError(message);
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (confirmDiscard(dirty)) {
      navigate(cameFromSprayInventory ? '/protection/stock-levels' : '/consumables/procurement');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleSave();
  };

  // ── render ───────────────────────────────────────────────────────────
  const titles = {
    source:     t('consumables.procurement.form.section.source'),
    product:    t('consumables.procurement.form.section.product'),
    quantity:   t('consumables.procurement.form.section.quantity'),
    financials: t('consumables.procurement.form.section.financials'),
  };

  const sections: FormShellSection[] = [
    { id: 'procurement-source',     title: titles.source },
    { id: 'procurement-product',    title: titles.product },
    { id: 'procurement-quantity',   title: titles.quantity },
    { id: 'procurement-financials', title: titles.financials },
  ];

  const saveBarStatus: SaveBarStatus = saving
    ? { kind: 'saving' }
    : apiError
      ? { kind: 'error', message: apiError }
      : dirty
        ? { kind: 'dirty' }
        : null;

  return (
    <div data-testid="consumables-procurement-form-page">
      <PageHeader title={t('consumables.procurement.newProcurement')} />

      <FormShell
        sections={sections}
        onSubmit={handleSubmit}
        saveBar={
          <SaveBar
            status={saveBarStatus}
            primaryAction={{
              label: t('consumables.procurement.form.save'),
              onClick: handleSave,
              disabled: !allRequiredFilled || saving || uploadingCount > 0,
            }}
            secondaryActions={[
              {
                label: t('consumables.procurement.form.cancel'),
                tone: 'subtle',
                onClick: handleCancel,
                disabled: saving,
              },
            ]}
          />
        }
      >
        <FormSection id="procurement-source" title={titles.source}>
          <div className="pat-field-grid pat-field-grid--2">
            <Field
              label={t('consumables.procurement.form.field.date')}
              required
              controlId="procurement-date"
            >
              <input
                id="procurement-date"
                type="date"
                className="pat-input"
                value={date}
                onChange={e => setDate(e.target.value)}
                required
              />
            </Field>
            <Field
              label={t('consumables.procurement.form.field.supplier')}
              required
              controlId="procurement-supplier"
            >
              <input
                id="procurement-supplier"
                type="text"
                className="pat-input"
                value={supplier}
                onChange={e => setSupplier(e.target.value)}
                required
              />
            </Field>
            <Field
              label={t('consumables.procurement.form.field.referenceNumber')}
              required
              controlId="procurement-reference"
            >
              <input
                id="procurement-reference"
                type="text"
                className="pat-input"
                value={referenceNumber}
                onChange={e => setReferenceNumber(e.target.value)}
                required
              />
            </Field>
          </div>
          {/* Attachments — hidden file input + "+ Add attachment" button +
              RowCard list. Uploads happen pre-submit; metadata is bundled
              into the POST body. The slot testid is preserved so other
              tests can resolve the section. */}
          <div
            data-testid="attachments-slot"
            aria-label={t('consumables.procurement.form.field.attachments')}
            style={{ marginTop: 12 }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_ATTACHMENT_TYPES}
              style={{ display: 'none' }}
              onChange={handleFilesPicked}
              data-testid="attachments-file-input"
              aria-label={t('consumables.procurement.form.field.attachments')}
            />
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={handlePickAttachments}
              disabled={uploadingCount > 0}
            >
              {uploadingCount > 0
                ? t('consumables.procurement.form.field.attachmentUploading', {
                    defaultValue: 'Uploading…',
                  })
                : t('consumables.procurement.form.field.addAttachment')}
            </button>
            {attachmentError && (
              <Notice tone="warn" className="mt-2" testId="attachments-error">
                {attachmentError}
              </Notice>
            )}
            {attachments.length > 0 && (
              <div
                style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}
                data-testid="attachments-list"
              >
                {attachments.map(a => (
                  <RowCard
                    key={a.s3Key}
                    icon="bi-paperclip"
                    title={a.fileName}
                    subtitle={`${a.mimeType} · ${formatBytes(a.size)}`}
                    onRemove={() => handleRemoveAttachment(a.s3Key)}
                    removeLabel={t('consumables.procurement.form.field.removeAttachment', {
                      defaultValue: 'Remove attachment',
                    })}
                    testId={`attachment-row-${a.s3Key}`}
                  />
                ))}
              </div>
            )}
          </div>
        </FormSection>

        <FormSection id="procurement-product" title={titles.product}>
          <div className="pat-field-grid pat-field-grid--2">
            <Field
              label={t('consumables.procurement.form.field.product')}
              required
              controlId="procurement-product-select"
            >
              <select
                id="procurement-product-select"
                className="pat-input"
                value={productId}
                onChange={e => handleSelectProduct(e.target.value)}
                required
              >
                <option value="">—</option>
                {products.map(p => (
                  <option key={p._id} value={p._id}>
                    {p.productName}
                    {isChemical(p) && p.lNumber ? ` (${p.lNumber})` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label={t('consumables.procurement.form.field.selectedType')}
              controlId="procurement-selected-type"
            >
              <input
                id="procurement-selected-type"
                type="text"
                className="pat-input"
                value={consumableType}
                readOnly
                disabled
              />
            </Field>
          </div>
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={() => setQuickCreateOpen(true)}
            >
              {t('consumables.procurement.form.field.newProduct')}
            </button>
          </div>
          {selectedProduct && (
            <p
              style={{ marginTop: 8, color: 'var(--shell-text-muted)' }}
              data-testid="selected-product-label"
            >
              {selectedProduct.productName}
              {isChemical(selectedProduct) && selectedProduct.lNumber
                ? ` · ${selectedProduct.lNumber}`
                : ''}
            </p>
          )}
        </FormSection>

        <FormSection id="procurement-quantity" title={titles.quantity}>
          <div className="pat-field-grid pat-field-grid--3">
            <Field
              label={t('consumables.procurement.form.field.quantity')}
              required
              controlId="procurement-quantity-input"
            >
              <input
                id="procurement-quantity-input"
                type="number"
                min={0}
                step="any"
                className="pat-input"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
                required
              />
            </Field>
            <Field
              label={t('consumables.procurement.form.field.unit')}
              required
              controlId="procurement-unit"
            >
              <select
                id="procurement-unit"
                className="pat-input"
                value={quantityUnit}
                onChange={e => setQuantityUnit(e.target.value)}
                required
                disabled={unitOptions.length === 0}
              >
                {unitOptions.length === 0 && <option value="">—</option>}
                {unitOptions.map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </Field>
            <Field
              label={t('consumables.procurement.form.field.expiryDate')}
              controlId="procurement-expiry"
            >
              <input
                id="procurement-expiry"
                type="date"
                className="pat-input"
                value={expiryDate}
                onChange={e => setExpiryDate(e.target.value)}
              />
            </Field>
          </div>
        </FormSection>

        <FormSection id="procurement-financials" title={titles.financials}>
          <div className="pat-field-grid pat-field-grid--2">
            <Field
              label={t('consumables.procurement.form.field.unitPrice')}
              required
              controlId="procurement-unit-price"
            >
              <input
                id="procurement-unit-price"
                type="number"
                min={0}
                step="any"
                className="pat-input"
                value={unitPrice}
                onChange={e => setUnitPrice(e.target.value)}
                required
              />
            </Field>
            <Field
              label={t('consumables.procurement.form.field.totalPrice')}
              required
              controlId="procurement-total-price"
            >
              <input
                id="procurement-total-price"
                type="number"
                min={0}
                step="any"
                className="pat-input"
                value={totalPrice}
                onChange={e => setTotalPrice(e.target.value)}
                required
              />
            </Field>
          </div>
          {/* Total-mismatch soft warning slot — shown only when all three
              figures are meaningful and drift exceeds 1%. SaveBar is NOT
              affected: this is purely informational. The slot element itself
              is preserved unconditionally so downstream hooks (Task 7.4+)
              can resolve it via testid. */}
          <div data-testid="total-mismatch-slot">
            {showTotalMismatch && (
              <Notice tone="warn" role="status">
                {t('consumables.procurement.form.financials.totalMismatchWarning', {
                  computed: computedTotal.toFixed(2),
                  delta: mismatchDelta.toFixed(2),
                  percent: ((mismatchDelta / computedTotal) * 100).toFixed(1),
                })}
              </Notice>
            )}
          </div>
        </FormSection>
      </FormShell>

      <QuickCreateConsumableModal
        open={quickCreateOpen}
        defaultType={consumableType || 'chemical'}
        onCancel={() => setQuickCreateOpen(false)}
        onCreated={handleProductCreated}
      />
    </div>
  );
};

export default ConsumableProcurementFormPage;
